import { useTrackEvent, ANALYTICS_EVENTS } from '@resida/analytics';
import { Breadcrumb } from './Breadcrumb';
import { RelatedFeatures } from './RelatedFeatures';
import { CTABanner } from './CTABanner';
import { FeatureScene } from './FeatureScene';
import { SEO } from '../SEO';
import type { FeaturePageData } from '../../data/features';

const isPreLaunch = import.meta.env.VITE_PRE_LAUNCH === 'true';

interface FeaturePageTemplateProps {
  feature: FeaturePageData;
}

export function FeaturePageTemplate({ feature }: FeaturePageTemplateProps): JSX.Element {
  const trackEvent = useTrackEvent();

  return (
    <>
      <SEO
        title={feature.seo.title}
        description={feature.seo.description}
        path={`/features/${feature.slug}`}
      />

      {/* Hero */}
      <section className="pt-12 pb-16 bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Features', href: '/features' },
              { label: feature.title },
            ]}
          />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] gap-10 lg:gap-16 items-center">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
                {feature.title}
              </h1>
              <p className="text-lg md:text-xl text-[var(--landing-text-muted)] leading-relaxed">
                {feature.subtitle}
              </p>
            </div>
            <div className="w-full max-w-md lg:max-w-none mx-auto lg:mx-0">
              <FeatureScene slug={feature.slug} />
            </div>
          </div>
        </div>
      </section>

      {/* Problem statement */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-sm font-medium uppercase tracking-widest text-[var(--landing-accent-text)] mb-4">
              The problem
            </h2>
            <p className="text-lg text-[var(--landing-text-secondary)] leading-relaxed">
              {feature.problem}
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--landing-text-primary)] text-center mb-12">
            How it works
          </h2>
          <div className="max-w-3xl mx-auto space-y-8">
            {feature.steps.map((step, i) => (
              <div key={i} className="flex gap-5">
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--landing-accent)] text-white flex items-center justify-center font-bold text-sm"
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-1">
                    {step.title}
                  </h3>
                  <p className="text-[var(--landing-text-muted)] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--landing-text-primary)] text-center mb-12">
            What&apos;s included
          </h2>
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {feature.capabilities.map((cap, i) => (
              <div
                key={i}
                className="flex items-start gap-3 text-sm text-[var(--landing-text-secondary)]"
              >
                <span
                  className="text-[var(--landing-accent-text)] font-bold flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  &#10003;
                </span>
                {cap}
              </div>
            ))}
          </div>

          {/* Inline CTA */}
          <div className="text-center mt-12" hidden={isPreLaunch}>
            <a
              href="/landlord/login"
              className="inline-flex items-center justify-center min-h-[44px] px-8 py-3 rounded-[var(--landing-radius)] bg-[var(--landing-accent)] hover:bg-[var(--landing-accent-hover)] text-white text-base font-semibold transition-colors shadow-[var(--landing-shadow)]"
              onClick={() =>
                trackEvent(ANALYTICS_EVENTS.LANDING_CTA_CLICKED, {
                  location: `feature_${feature.slug}`,
                })
              }
            >
              Start Free Trial
            </a>
          </div>
        </div>
      </section>

      {/* Related features */}
      <RelatedFeatures slugs={feature.relatedFeatures} />

      {/* Closing CTA */}
      <CTABanner />
    </>
  );
}
